// src/admin/pages/AdminDashboard.jsx
//
// Route: /dashboard/admin
//
// Founder / Financial Overview
// Hierarchy:
// Date -> KPIs -> Financial Charts -> Client Performance -> Clients -> Camps

import React, { useState, useEffect, useCallback } from 'react';
import { getAdminDashboard } from './adminApi';
import { formatDateYMD } from './adminFormatters';

import AdminHeader from './AdminHeader';
import AdminKpiCards from './AdminKpiCards';
import AdminCharts from './AdminCharts';
import ClientAccordion from './ClientAccordion';
import LoadingState from './LoadingState';
import ErrorState from './ErrorState';
import EmptyState from './EmptyState';

import './adminDashboard.css';

function getErrorMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.message ||
    'Something went wrong while loading the dashboard.'
  );
}

export default function AdminDashboard() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback((date) => {
    setLoading(true);
    setError(null);

    getAdminDashboard(formatDateYMD(date))
      .then((response) => {
        setData(response);
      })
      .catch((err) => {
        setData(null);
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchDashboard(selectedDate);
  }, [selectedDate, fetchDashboard]);

  const handleRetry = () => {
    fetchDashboard(selectedDate);
  };

  const clients = data?.clients || [];

  const hasNoCamps =
    !loading &&
    !error &&
    clients.length === 0;

  return (
    <div className="admin-page min-h-screen">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">

        {/* Header */}
        <AdminHeader
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
        />

        {/* Loading */}
        {loading && (
          <LoadingState variant="dashboard" />
        )}

        {/* Error */}
        {!loading && error && (
          <ErrorState
            title="Unable to load dashboard."
            message={getErrorMessage(error)}
            onRetry={handleRetry}
          />
        )}

        {/* Dashboard */}
        {!loading && !error && data && (
          <>
            {/* KPI CARDS */}
            <AdminKpiCards
              summary={data.summary}
            />

            {/* CHARTS */}
            <AdminCharts
              summary={data.summary}
              clients={clients}
            />

            {/* CLIENTS */}
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6B7C7C]">
                    Clients
                  </h2>

                  <p className="mt-1 text-sm text-gray-500">
                    View financial and operational details by client
                  </p>
                </div>

                {!hasNoCamps && (
                  <div className="rounded-full bg-[#F1F7F6] px-3 py-1 text-xs font-medium text-[#437B78]">
                    {clients.length} Client
                    {clients.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {hasNoCamps ? (
                <EmptyState
                  title="No camps found"
                  description="There are no active camps for this date."
                />
              ) : (
                <div className="space-y-3">
                  {clients.map((client) => (
                    <ClientAccordion
                      key={client.client_id}
                      client={client}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}